import { useAdminContext } from '@/contexts/AdminContext';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { billingService } from '@common/api/billingService';
import Icon from '@common/components/Icon';
import { Button } from '@common/index';
import {
    Avatar, AvatarFallback, AvatarImage
} from '@radix-ui/react-avatar';
import { ArrowTopRightIcon } from '@radix-ui/react-icons';
import cls from 'classnames';
import React, {
    useEffect,
    useRef,
    useState
} from 'react';
import AdminProfileModal, { NavKey } from './AdminProfileModal';
import styles from './UserMenu.module.scss';



const UserMenu: React.FC = () => {
    const {
        user, profile, logout, openSignInModal, userBilling, openJoinBetaModal, isBetaAccess, openPricingModal
    } = useAuth();
    const { modal } = useAdminContext();
    const {userPoints, planText} = userBilling;
    const [isMenuHoverPopupOpen, setIsMenuHoverPopupOpen] = useState(false);
    const [isPlanCardPopupOpen, setIsPlanCardPopupOpen] = useState(false);
    const { showToast } = useToast();
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsMenuHoverPopupOpen(false);
            }
        };

        if (isMenuHoverPopupOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMenuHoverPopupOpen]);
    
    const handleLogout = () => {
        logout();
    };

    const handleLogin = () => {
        openSignInModal();
    };

    const openModal = (tab: NavKey) => {
        modal.openModal('profile', {
            activeTab: tab,
            onClose: () => {
                setIsMenuHoverPopupOpen(false);
            }
        });
        setIsMenuHoverPopupOpen(false);
    };


    // 如果用户未登录，显示登录按钮
    if (!user) {
        return (
            <div className={styles.userMenuContainer}>
                <button 
                    className={styles.loginButton}
                    onClick={handleLogin}
                    type="button"
                >
                    <span>Sign In</span>
                </button>
            </div>
        );
    }

    const userName = profile?.userName || user?.email || 'User';
    const userEmail = user?.email || '';
    
    // 使用真实的积分数据，如果还在加载则显示加载状态
    const displayPoints = userPoints?.total_remaining || userPoints?.balance_points || 0;

    const handleUpgrade = () => {
        if(!isBetaAccess){
            openJoinBetaModal();
            return;
        }
        openPricingModal();
        setIsMenuHoverPopupOpen(false);
        setIsPlanCardPopupOpen(false);
    };

    const handleCancelSubscription = async() => {
        try{
            const response = await billingService.createSubscriptionPortal(window.location.href);
            if(response.url){
                window.location.href = response.url;
            }else{
                showToast('Failed to cancel subscription', 'error');
            }
        }catch(error){
            showToast('Failed to cancel subscription', 'error');
            console.log('error', error);
        }
        setIsMenuHoverPopupOpen(false);
        setIsPlanCardPopupOpen(false);
    }
    return (
        <div className={styles.userMenuContainer}>
            {/* 积分显示区域 
            <div 
                className={styles.pointsSection}
                tabIndex={0}
                role="button"
                aria-label="View credits details"
                onMouseEnter={() => setIsPlanCardPopupOpen(true)}
                onMouseLeave={() => setIsPlanCardPopupOpen(false)}
            >
                <div className={styles.upgrade} onClick={isCanceled ? handleCancelSubscription : handleUpgrade}>
                    {isCanceled ? 'Resubscribe' : 'Upgrade'}
                </div>
                <Separator
                    orientation="vertical"
                    style={{
                        backgroundColor: '#E5E7EB', width: 1, height: 14
                    }}
                />
                <div className={styles.pointsIcon}>
                    <Icon name="credit_color" size={20} />
                    <span className={styles.pointsText}>{displayPoints}</span>
                </div>
                
                <div className={cls(styles.planCardHoverPopup, {[styles.active]: isPlanCardPopupOpen})}>
                    <PlanCard onClose={() => setIsPlanCardPopupOpen(false)} />
                    <div className={styles.planCardFooter} onClick={handleUsageDetails}>
                        Usage details
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M6.1137 14.1364C5.76223 13.7849 5.76223 13.2151 6.1137 12.8636L9.97731 9.00002L6.1137 5.13642C5.76223 4.78495 5.76223 4.2151 6.1137 3.86363C6.46517 3.51216 7.03502 3.51216 7.38649 3.86363L11.8865 8.36363C12.238 8.7151 12.238 9.28495 11.8865 9.63642L7.38649 14.1364C7.03502 14.4879 6.46517 14.4879 6.1137 14.1364Z" fill="#1A162F" />
                        </svg>
                    </div>
                </div>
            </div>
            */}

            {/* 头像区域 */}
            <div 
                ref={menuRef}
                className={styles.userAvatarSection}
                tabIndex={0}
                role="button"
                aria-haspopup="true"
                aria-label="User menu"
                onClick={() => setIsMenuHoverPopupOpen(!isMenuHoverPopupOpen)}
            >
                <Avatar className={styles.userAvatar}>
                    <AvatarImage 
                        className={styles.avatarImage}
                        src={user?.user_metadata?.avatar_url || user?.user_metadata?.picture} 
                        alt={userName}
                    />
                    <AvatarFallback className={styles.avatarFallback}>
                        <Icon name="defaultAvatar" size={36} />
                    </AvatarFallback>
                </Avatar>

                {/* 菜单 Hover 弹窗 */}
                <div 
                    className={cls(styles.menuHoverPopup, {[styles.active]: isMenuHoverPopupOpen})}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className={styles.menuHeader}>
                        <Avatar className={styles.menuAvatar}>
                            <AvatarImage 
                                className={styles.avatarImage}
                                src={user?.user_metadata?.avatar_url || user?.user_metadata?.picture} 
                                alt={userName}
                            />
                            <AvatarFallback className={styles.avatarFallback}>
                                <Icon name="defaultAvatar" size={36} />
                            </AvatarFallback>
                        </Avatar>
                        <div className={styles.menuUserInfo}>
                            <div className={styles.menuUserInfoHeader}>
                                <span className={styles.menuUserName}>{userName}</span>
                            </div>
                            <span className={styles.menuUserEmail}>{userEmail}</span>
                        </div>
                    </div>
                    
                    <div className={styles.menuCategory}>Account</div>
                    
                    {/* Profile 菜单项 */}
                    <div
                        className={styles.menuItem}
                        onClick={() => openModal(NavKey.profile)}
                        tabIndex={0}
                        role="menuitem"
                        aria-label="Profile"
                    >
                        <Icon name="profile" size={20} />
                        <span className={styles.menuText}>Profile</span>
                    </div>

                    <div className={styles.menuSeparator}></div>
                    <div className={styles.menuCategory}>Subscription</div>
                    {/* Billing 菜单项 */}
                    <div 
                        className={styles.planItem} 
                        tabIndex={0}
                        role="menuitem"
                        aria-label="Subscription"
                    >
                        <span className={styles.planName}>{planText.planDisplayText}</span>
                        {planText.buttonText === 'Upgrade'
                            ? (
                                <Button
                                    size="2"
                                    color="primary"
                                    onClick={() => {
                                        openPricingModal()
                                        setIsMenuHoverPopupOpen(false)
                                    }}
                                >
                                    {planText.buttonText}
                                </Button>
                            ): (
                                <Button 
                                    variant="outline"
                                    size="2"
                                    color="gray"
                                    onClick={() => {
                                        openPricingModal()
                                        setIsMenuHoverPopupOpen(false)
                                    }}
                                >
                                    {planText.buttonText}
                                </Button>
                            ) }
                    </div>

                    <div
                        className={styles.menuItem}
                        onClick={() => openModal(NavKey.billing)}
                        tabIndex={0}
                        role="menuitem"
                        aria-label="Subscription"
                    >
                        <Icon name="billing" size={20} />
                        <span className={styles.menuText}>Plan & Billing</span>
                    </div>

                    <div className={styles.menuSeparator}></div>
                    <div className={styles.menuCategory}>Brand</div>
                    <div
                        className={styles.menuItem}
                        onClick={() => openModal(NavKey.brand)}
                        tabIndex={0}
                        role="menuitem"
                        aria-label="Brand"
                    >
                        <Icon name="brand" size={20} />
                        <span className={styles.menuText}>Basic information</span>
                    </div>
                    <div
                        className={styles.menuItem}
                        onClick={() => openModal(NavKey.integration)}
                        tabIndex={0}
                        role="menuitem"
                        aria-label="Integrations"
                    >
                        <Icon name="connect" size={20} />
                        <span className={styles.menuText}>Intergrations</span>
                    </div>

                    <div className={styles.menuSeparator}></div>
                    <div 
                        className={styles.menuItem} 
                        onClick={() => {
                            setIsMenuHoverPopupOpen(false)
                            window.open('/?index=1', '_blank')
                        }}
                        tabIndex={0}
                        role="menuitem"
                        aria-label="Home page"
                        style={{justifyContent: 'space-between'}}
                    >
                        <div
                            style={{
                                display: 'flex', alignItems: 'center', gap: 4
                            }}
                        >
                            <Icon name="home" size={20} />
                            <span className={styles.menuText}>Home page</span>
                        </div>
                        <ArrowTopRightIcon width={16} height={16} />
                    </div>

                    {/* Log out 菜单项 */}
                    <div className={styles.menuSeparator}></div>

                    <div 
                        className={styles.menuItem} 
                        onClick={handleLogout}
                        tabIndex={0}
                        role="menuitem"
                        aria-label="Log out"
                    >
                        <Icon name="logout" size={20} color="#F26D6D" />
                        <span className={styles.menuText} style={{color: '#F26D6D'}}>Log out</span>
                    </div>
                </div>
            </div>
            
            {/* Profile Modal */}
            <AdminProfileModal />
        </div>
    );
};

export default UserMenu;